import React, { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { getTooltip } from "utils/getMessages";

import { ScheduledJobProto } from "build-ts/schedule";
import CronBuilder from "components/analyst/CronBuilder";
import WorkflowSectionHeading from "modules/analyst/components/WorkflowSectionHeading";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";
import { JobFormView } from "../containers/WorkflowDetail";

export interface WorkflowDetailProps {
  jobFormView: JobFormView;
  trigger: string;
  analysisInstructions: string;
  reportingInstructions: string;
  cronExpression: string;
  setCronExpression: React.Dispatch<React.SetStateAction<string>>;
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  selectedJobDetails: ScheduledJobProto | undefined;
}

const WorkflowDetail: React.FC<WorkflowDetailProps> = ({
  jobFormView,
  trigger,
  analysisInstructions,
  reportingInstructions,
  cronExpression,
  setCronExpression,
  isActive,
  setIsActive,
  selectedJobDetails,
}) => {
  const [guidanceExpanded, setGuidanceExpanded] = useState(true);

  // ── Guidance tab view (read-only, collapsible) ──
  if (jobFormView === "guidance") {
    return (
      <Flex direction="column" flex="1" minH={0} minW={0} overflow="hidden" px={2}>
        <Box
          maxW="900px"
          mx="auto"
          w="100%"
          flex="1"
          minH={0}
          minW={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          px={4}
          py={4}
        >
          {/* Heading — fixed, never scrolls */}
          <Box flexShrink={0} cursor="pointer" onClick={() => setGuidanceExpanded(p => !p)}>
            <WorkflowSectionHeading
              label="For Internal Use Only"
              tooltip="Internal guidance for the agent to perform workflow tasks more reliably."
              rightSlot={
                <Icon
                  as={guidanceExpanded ? AppIcons.chevronUp : AppIcons.chevronDown}
                  boxSize={3.5}
                  color="gray.500"
                />
              }
            />
          </Box>

          {/* Scroll region — absolute fill avoids Chrome flex min-height:auto overflow bugs */}
          {guidanceExpanded && (
            <Box position="relative" flex="1" minH={0} minW={0} mt={2}>
              <Box
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                right={0}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={4}
                py={3}
                bg="gray.50"
                overflowY="auto"
                overflowX="auto"
                sx={{ overscrollBehavior: "contain" }}
              >
                {analysisInstructions.trim() ? (
                  <MarkdownRenderer content={analysisInstructions} />
                ) : (
                  <Text fontSize="sm" color="gray.400" mb={0}>
                    No guidance set.
                  </Text>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Flex>
    );
  }

  // ── Process / Workflow tab view ──
  return (
    <Flex direction="column" flex="1" minH={0} h="100%" overflow="hidden" px={2}>
      <Box maxW="900px" mx="auto" w="100%" flex="1" minH={0} overflowY="auto" px={4} py={4}>
        <VStack spacing={5} align="stretch">
          {/* 1. Objective — read-only, collapsible */}
          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem border="none">
              <AccordionButton px={0} py={0} _hover={{ bg: "transparent" }} w="100%">
                <WorkflowSectionHeading
                  label="Objective"
                  tooltip={getTooltip("workflow_detail.objective.tooltip")}
                  rightSlot={<AccordionIcon color="gray.500" />}
                />
              </AccordionButton>
              <AccordionPanel px={0} pb={4}>
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={2}
                  py={1}
                  bg="white"
                  overflowY="auto"
                  overflowX="auto"
                  minH="80px"
                >
                  {trigger.trim() ? (
                    <MarkdownRenderer content={trigger} />
                  ) : (
                    <Text fontSize="sm" color="gray.400" mb={0}>
                      No objective set.
                    </Text>
                  )}
                </Box>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          {/* 2. Schedule — editable; CronBuilder handles its own save for existing jobs */}
          <Box>
            <WorkflowSectionHeading label="Schedule" tooltip={getTooltip("workflow_detail.schedule.tooltip")} />
            <CronBuilder
              cronExpression={cronExpression}
              setCronExpression={setCronExpression}
              isActive={isActive}
              setIsActive={setIsActive}
              jobDetails={selectedJobDetails}
            />
          </Box>

          {/* 3. Instructions — read-only, collapsible */}
          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem border="none">
              <AccordionButton px={0} py={0} _hover={{ bg: "transparent" }} w="100%">
                <WorkflowSectionHeading
                  label="Instructions"
                  tooltip={getTooltip("workflow_detail.instructions.tooltip")}
                  rightSlot={<AccordionIcon color="gray.500" />}
                />
              </AccordionButton>
              <AccordionPanel px={0} pb={4}>
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={2}
                  py={1}
                  bg="white"
                  overflowY="auto"
                  overflowX="auto"
                  minH="80px"
                >
                  {reportingInstructions.trim() ? (
                    <MarkdownRenderer content={reportingInstructions} />
                  ) : (
                    <Text fontSize="sm" color="gray.400" mb={0}>
                      No instructions set.
                    </Text>
                  )}
                </Box>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </VStack>
      </Box>
    </Flex>
  );
};

export default WorkflowDetail;
